/**
 * Everything non-React behind the Run Brand Safety Check modal (AAPU-6214):
 * the backend contract, the copy, the phase machine, and the validation bounds.
 *
 * The per-check cost estimate + credit balance come from `POST creator/brand-safety/availability`
 * (see `readAvailability`); `GET /workspace/usage` is only the fallback until that resolves.
 *
 * The run endpoint is live. The `.checks.updated` Reverb event (channel `brand-safety.{campaignId}`,
 * `App\Events\BrandSafety\BrandSafetyChecksUpdated`) is documented and wired up; the other Reverb
 * event names below are still assumptions, marked `TODO(BE)`. Keeping them here means that when the
 * rest of the backend spec lands, this is the only file that needs updating.
 */

import moment from 'moment';
import { isApiSuccess, QUOTA_EXCEEDED } from '../../../../constants/platformStatusCodes';
import {
  BRAND_SAFETY_ENDPOINTS as BS_EP,
  apiPost,
  buildRunBody,
} from '../../../BrandSafety/brandSafetyApi';

// ─── Endpoints ───────────────────────────────────────────────────────────────
// The run now targets the documented `POST creator/brand-safety/run` (via BS_EP.run + apiPost); the
// old guessed path below is kept only for reference and is no longer called.
export const BS_ENDPOINTS = {
  RUN: '/persona/runBrandSafety', // DEPRECATED — superseded by BS_EP.run
};

// ─── Wire enums and formats ──────────────────────────────────────────────────
export const BS_CHECK_TYPE = { FULL: 'full', CUSTOM: 'custom' };

// Every scope here must map to a documented `checkType` (`content_count` / `time_range`). A
// "track until turned off" scope was removed: continuous tracking has no endpoint, so it could
// neither be estimated (availability) nor started (run).
export const BS_SCOPE = {
  CONTENT_COUNT: 'contentCount',
  TIME_RANGE: 'timeRange',
};

/** What the user sees and types (per the design). */
export const BS_DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';
/** What goes on the wire — `MM/DD/YYYY` is locale-ambiguous over HTTP. TODO(BE) confirm. */
export const BS_DATE_PAYLOAD_FORMAT = 'YYYY-MM-DD';

// ─── Bounds ──────────────────────────────────────────────────────────────────
export const MIN_POSTS = 1;
export const DEFAULT_POSTS_COUNT = 50;

// ─── Credits ─────────────────────────────────────────────────────────────────
/**
 * Feature key under `state.Workspace.workspaceUsage`, populated by `getUsage()` →
 * `GET /workspace/usage`, shaped `{ used, limit, remaining }`. This is the only
 * source of the credit balance.
 */
export const BS_USAGE_KEY = 'brand_safety';

/**
 * Reads the balance out of the workspace usage payload, reusing the limit semantics
 * already encoded in `Workspace/utils/usageCardUtils.js`: `-1` is unlimited, `0`
 * means the feature is not on the plan.
 *
 * `known: false` is the fail-open case — usage not loaded, or `getUsage()` skipped
 * because the workspace UI is hidden. The run must NOT be blocked then; the balance
 * line is simply omitted.
 */
export function resolveCreditBalance(workspaceUsage) {
  const unknown = { known: false, remaining: null, unlimited: false, blocked: false };

  const row = workspaceUsage?.[BS_USAGE_KEY];
  if (!row || typeof row !== 'object') return unknown;

  const { limit, remaining } = row;

  if (limit === -1) return { known: true, remaining: null, unlimited: true, blocked: false };
  if (limit === 0) return { known: true, remaining: 0, unlimited: false, blocked: true };

  if (remaining == null || Number.isNaN(Number(remaining))) return unknown;

  return { known: true, remaining: Number(remaining), unlimited: false, blocked: false };
}

/**
 * Whether a workspace has NO brand-safety credit restriction — an internal/"hidden" workspace or one on
 * an unlimited plan. For these the modal skips the usage fetch, the credit gate, and the credit panel.
 * Driven by the live `/auth/me` workspace (`state.login.workspaceData ?? session.workspace`), NOT the
 * build-time tenant, so a hidden/unlimited user is never gated on credits regardless of which brand the
 * app was built as — matching the `status === 'hidden'` convention used elsewhere (e.g. CompetitiveIntel).
 */
export const isUnrestrictedWorkspace = (workspace) => {
  const status = String(workspace?.status ?? '').toLowerCase();
  const plan = String(workspace?.plan ?? '').toLowerCase();
  return status === 'hidden' || plan.startsWith('unlimited');
};

// ─── Response adapters ───────────────────────────────────────────────────────
/** The one place that knows how a creator report id is spelled, REST or websocket. */
export const readCreatorReportDetailsId = (source) =>
  String(
    source?.creatorReportDetailsId ??
      source?.creator_report_details_id ??
      source?.crd ??
      source?.id ??
      ''
  );

/**
 * Normalizes the run response. Tolerates an all-accepted 200 and a partial 207
 * carrying per-creator results. TODO(BE) confirm the results array name.
 */
export const readRunAccepted = (resp, requestedIds = []) => {
  const data = resp?.data ?? resp ?? {};
  const rows = [data.results, data.creators, data.recipients, data.items].find((candidate) =>
    Array.isArray(candidate)
  );

  const batchId = data.batchId ?? data.batch_id ?? null;

  if (!rows) {
    return { acceptedIds: requestedIds.map(String), rejectedIds: [], batchId, firstReason: null };
  }

  const acceptedIds = [];
  const rejectedIds = [];
  let firstReason = null;

  rows.forEach((row) => {
    const id = readCreatorReportDetailsId(row);
    const accepted = row?.accepted ?? row?.success ?? row?.queued ?? true;

    if (accepted) {
      acceptedIds.push(id);
      return;
    }

    rejectedIds.push(id);
    if (!firstReason) firstReason = row?.message ?? row?.error ?? row?.reason ?? null;
  });

  return { acceptedIds, rejectedIds, batchId, firstReason };
};

export const isBrandSafetyQuotaError = (resp) =>
  resp?.status === QUOTA_EXCEEDED ||
  resp?.httpStatus === 402 ||
  resp?.code === 'INSUFFICIENT_CREDITS';

/**
 * Whether a run submission was rate-limited (HTTP 429). AAPU-6693: the transport already retries 429s
 * (RETRYABLE_STATUS_CODES) with backoff, so this classifies the *final* failure after those retries so
 * the user gets a distinct "you're going too fast" message instead of the generic start-failed copy.
 */
export const isBrandSafetyRateLimited = (resp) => resp?.httpStatus === 429;

// ─── Doc contract mapping (checkType/scope → the documented run/availability body) ─────
/**
 * Modal state → the documented `checkType` (`full | content_count | time_range`). `null` means "no
 * valid selection yet" — every selectable scope maps to a real checkType, so a `null` here gates the
 * availability call (nothing to estimate) rather than describing an unsupported option.
 */
export function mapModalCheckType(values = {}) {
  const { checkType, scope } = values;
  if (checkType === BS_CHECK_TYPE.FULL) return 'full';
  if (checkType === BS_CHECK_TYPE.CUSTOM) {
    if (scope === BS_SCOPE.CONTENT_COUNT) return 'content_count';
    if (scope === BS_SCOPE.TIME_RANGE) return 'time_range';
  }
  return null;
}

/** The scope fields the doc allows for a given documented checkType (prohibited_unless). */
export function scopeFromValues(values = {}, docCheckType) {
  if (docCheckType === 'content_count') {
    return values.postsCount != null ? { maxPostsPerInfluencer: Number(values.postsCount) } : {};
  }
  if (docCheckType === 'time_range') {
    return {
      startDate: values.startDate ? values.startDate.format(BS_DATE_PAYLOAD_FORMAT) : null,
      endDate: values.endDate ? values.endDate.format(BS_DATE_PAYLOAD_FORMAT) : null,
    };
  }
  return {};
}

const acceptedFromIds = (ids, batchId) => ({
  acceptedIds: ids,
  rejectedIds: [],
  batchId,
  firstReason: null,
});

/**
 * Normalizes one entry of the availability response's `creators` array. Tolerates snake_case as a
 * fallback in case the backend ever sends it, matching this file's other response adapters.
 */
export function readAvailabilityCreator(raw) {
  const c = raw && typeof raw === 'object' ? raw : {};
  return {
    username: c.username ?? null,
    socialNetwork: c.socialNetwork ?? c.social_network ?? null,
    availablePostCount: c.availablePostCount ?? c.available_post_count ?? null,
    postsToCheck: c.postsToCheck ?? c.posts_to_check ?? null,
    hasAvailableContent: c.hasAvailableContent ?? c.has_available_content ?? null,
    countIsExact: c.countIsExact ?? c.count_is_exact ?? null,
    // 'full' (whole history) | 'requested' (capped maxPostsPerInfluencer) | 'pending' (not resolved yet)
    countMethod: c.countMethod ?? c.count_method ?? null,
    pending: Boolean(c.pending),
  };
}

/**
 * Normalize the `POST creator/brand-safety/availability` response `data` into one flat shape the modals
 * consume. The current backend puts credits at the top of `data` (`estimatedCredits`, `creditsRemaining`)
 * with an explicit `sufficientCredits` flag, a `smallestAvailablePostCount` cap, a top-level `pending`
 * flag, and per-creator info. The legacy nested `credits` block (`{ remaining, limit, estimatedCredits }`)
 * is tolerated as a fallback.
 *
 * `pending: true` means at least one creator's BrightData lookup didn't resolve inside the request's time
 * budget — `estimatedCredits`/`creditsRemaining` above only cover the resolved creators, so callers must
 * treat the totals as provisional and re-request (see `useBrandSafetyAvailability`) rather than trusting
 * them as final.
 */
export function readAvailability(data) {
  const d = data && typeof data === 'object' ? data : {};
  const credits = d.credits && typeof d.credits === 'object' ? d.credits : {};
  return {
    estimatedCredits: d.estimatedCredits ?? credits.estimatedCredits ?? null,
    creditsRemaining: d.creditsRemaining ?? credits.remaining ?? null,
    sufficientCredits: d.sufficientCredits,
    smallestAvailablePostCount: d.smallestAvailablePostCount ?? null,
    pending: Boolean(d.pending),
    creators: Array.isArray(d.creators) ? d.creators.map(readAvailabilityCreator) : [],
  };
}

// ─── Per-creator status (bulk modal row rendering) ────────────────────────────
export const BS_CREATOR_STATUS = { RESOLVED: 'resolved', PENDING: 'pending', STUCK: 'stuck' };

/** Matches a modal target to its normalized row in `availability.creators`, case-insensitively. */
export function findAvailabilityCreator(creators = [], target = {}) {
  const uname = String(target?.username ?? '').toLowerCase();
  if (!uname) return null;
  const net = String(target?.socialNetwork ?? '').toLowerCase();
  return (
    creators.find(
      (c) =>
        String(c.username ?? '').toLowerCase() === uname &&
        (!net || String(c.socialNetwork ?? '').toLowerCase() === net)
    ) ?? null
  );
}

/**
 * `null` — no availability data yet for this creator. `RESOLVED` — settled (covers the "no BrightData
 * record" zero-content case too). `PENDING` — still resolving, poll budget not exhausted. `STUCK` — still
 * pending after the poll budget ran out (surfaced as "Couldn't check").
 */
export function creatorAvailabilityStatus(creatorRow, { pollExhausted = false } = {}) {
  if (!creatorRow) return null;
  if (!creatorRow.pending) return BS_CREATOR_STATUS.RESOLVED;
  return pollExhausted ? BS_CREATOR_STATUS.STUCK : BS_CREATOR_STATUS.PENDING;
}

/** `"~N posts"` from a resolved creator row, or `null` when there is nothing to show yet. */
export function creatorPostsLabel(creatorRow) {
  const n = creatorRow?.postsToCheck ?? creatorRow?.availablePostCount ?? null;
  if (n == null) return null;
  return `~${Number(n).toLocaleString()} post${Number(n) === 1 ? '' : 's'}`;
}

/**
 * Reads the spendable balance from a normalized availability object (`readAvailability`). Credits now
 * live flat on `data` (`creditsRemaining`) with an explicit `sufficientCredits` flag; a null remaining
 * with an estimate present is treated as an unlimited plan, and `Run` is blocked when the backend says
 * credits are insufficient (or the balance is exactly 0).
 */
export function resolveAvailabilityBalance(availability) {
  const unknown = {
    known: false,
    remaining: null,
    unlimited: false,
    blocked: false,
    insufficient: false,
  };
  if (!availability || typeof availability !== 'object') return unknown;

  const { creditsRemaining, estimatedCredits, sufficientCredits } = availability;

  // A successful availability response means the plan IS enabled, so `blocked` (plan-not-available,
  // from the workspace-usage path) stays false; a shortfall is `insufficient` instead, which the modal
  // renders as "no credits remaining" rather than the misleading "not available on your plan".
  if (creditsRemaining == null) {
    // No remaining reported: unlimited plan when a cost was still estimated; otherwise unknown.
    return estimatedCredits != null
      ? {
          known: true,
          remaining: null,
          unlimited: true,
          blocked: false,
          insufficient: sufficientCredits === false,
        }
      : unknown;
  }
  if (Number.isNaN(Number(creditsRemaining))) return unknown;

  const rem = Number(creditsRemaining);
  return {
    known: true,
    remaining: rem,
    unlimited: false,
    blocked: false,
    insufficient: sufficientCredits === false || rem === 0,
  };
}

/**
 * The credit estimate to display for the currently requested post count, recomputed locally from the one
 * availability response (which fires once per modal opening) so the "This check will use ~N credits" label
 * tracks the input without re-hitting the endpoint. Scales the response's per-post rate — the aggregate
 * `estimatedCredits` ÷ the posts it actually priced (`Σ postsToCheck`) — by how many posts the current
 * count would scan (`Σ min(count, availablePostCount)`, capped per creator). Uniform for one creator
 * (single modal) or many (bulk); collapses to the response's own number when the requested count matches
 * what was priced. `~` in the copy already signals this is an approximation (assumes credits scale
 * linearly with posts). Falls back to the response's estimate when there's no count to scale by (full /
 * time-range checks) or the rate can't be derived.
 */
export function estimateCreditsForPostCount(availability, postsCount) {
  const base = availability?.estimatedCredits ?? null;
  if (base == null) return null;

  // No count to scale by (full / time-range check) — the response's estimate is already right.
  if (postsCount == null || !Number.isFinite(Number(postsCount))) return base;

  const creators = Array.isArray(availability.creators) ? availability.creators : [];
  const pricedPosts = creators.reduce((sum, c) => sum + (Number(c.postsToCheck) || 0), 0);
  if (pricedPosts <= 0) return base; // no per-post rate to derive — keep the response's estimate

  const requested = Math.max(0, Math.floor(Number(postsCount)));
  const scannedPosts = creators.reduce((sum, c) => {
    const available = c.availablePostCount;
    return sum + (available != null ? Math.min(requested, Number(available)) : requested);
  }, 0);

  return Math.round((base / pricedPosts) * scannedPosts);
}

// ─── API ─────────────────────────────────────────────────────────────────────
/**
 * Start the check via `POST creator/brand-safety/run`. Resolves for both success and failure so the
 * caller branches on `ok`. A real quota rejection is surfaced as a failure (the api client already
 * raises the global QuotaExceededModal). The optimistic seed keys off the targets' own crds, so the
 * run response need not echo `creatorReportDetailsId`.
 */
export const submitBrandSafetyRun = ({ targets, values }) =>
  new Promise((resolve) => {
    const requestedIds = targets.map((target) => String(target.creatorReportDetailsId));
    const docCheckType = mapModalCheckType(values);

    // Unreachable from the UI (the Run button is gated on a valid scope), but never pretend a run
    // started when there is no checkType to send — that would flip rows to "Checking…" forever.
    if (docCheckType == null) {
      resolve({ ok: false, resp: null, accepted: null });
      return;
    }

    const body = buildRunBody({
      checkType: docCheckType,
      creators: targets.map((target) => ({
        username: target.username,
        socialNetwork: target.socialNetwork,
        creatorReportDetailsId: target.creatorReportDetailsId,
      })),
      ...scopeFromValues(values, docCheckType),
    });

    apiPost(BS_EP.run, body).then((resp) => {
      // A genuine quota rejection is surfaced as a failure.
      if (isBrandSafetyQuotaError(resp)) {
        resolve({ ok: false, resp, accepted: null });
        return;
      }
      const ok = isApiSuccess(resp);
      const batchId = resp?.data?.runUuid ?? null;
      resolve({ ok, resp, accepted: ok ? acceptedFromIds(requestedIds, batchId) : null });
    });
  });

// ─── Creator-record reads ────────────────────────────────────────────────────
// Per-creator check statuses (the row's latest check, from `.checks.updated` / `row.brandSafetyCheck`).
const RUNNING_CHECK_STATES = ['queued', 'downloading_posts', 'running', 'processing'];
const FAILED_CHECK_STATES = ['failed', 'cancelled'];
// Terminal, but only some posts were scanned: the report is viewable and the check keeps going.
export const PARTIAL_CHECK_STATE = 'partially_succeeded';

/**
 * The row's EXPLICIT brand-safety check status (lowercased) — the status the backend actually reports
 * on the user-run check (preferred) or the automatic pipeline scan — or `null` when neither carries a
 * status. Unlike `readBrandSafetyCheck`/`mergeBrandSafetyCheck`, this does NOT fall back to the legacy
 * flat `brandSafety` flag counts and does NOT default an absent status to `succeeded`, so a row that
 * only has baseline flags (no live check) reads as `null` rather than a phantom terminal state. Used to
 * reconcile an in-flight run to completion without false-completing on baseline data.
 */
export const readExplicitCheckStatus = (record) => {
  const raw =
    record?.brandSafetyCheck?.userCheck?.status ?? record?.brandSafetyCheck?.pipelineCheck?.status;
  return raw != null && raw !== '' ? String(raw).toLowerCase() : null;
};

/** Whether a check status is one of the in-progress states (still running). */
export const isRunningCheckStatus = (status) => RUNNING_CHECK_STATES.includes(status);

/** Whether a check status is terminal: succeeded / partially_succeeded / failed / cancelled. */
export const isTerminalCheckStatus = (status) =>
  status === 'succeeded' || status === PARTIAL_CHECK_STATE || FAILED_CHECK_STATES.includes(status);

/**
 * Total flags on a check — the SUM of the per-category counts, so `{ TrademarkFlag: 4 }` → 4 and
 * `{ TrademarkFlag: 4, AlcoholFlag: 2 }` → 6. Empty (`{}` or `[]`) → 0.
 */
export const brandSafetyFlagTotal = (flags) => {
  if (!flags || typeof flags !== 'object') return 0;
  const values = Array.isArray(flags) ? flags : Object.values(flags);
  return values.reduce((sum, v) => sum + (Number(v) || 0), 0);
};

/**
 * Merges a `{ pipelineCheck, userCheck }` pair into the one check to render → `{ status, flags,
 * source, totalPostsScanned, scannedAt, checkType } | null`. This is the shape carried both by
 * `row.brandSafetyCheck` on a fetchCreators row AND by each `.checks.updated` creatorChecks entry
 * (`readChecksUpdatedEvent`), so both call this — one merge priority for the REST snapshot and the
 * live push alike. `userCheck` (a user-run full/custom check) is the primary source; the automatic
 * background `pipelineCheck` (a lower-coverage sample scan) is the fallback shown until the user runs
 * a real check.
 */
export function mergeBrandSafetyCheck({ pipelineCheck, userCheck } = {}) {
  if (userCheck && typeof userCheck === 'object') {
    return {
      status: String(userCheck.status ?? '').toLowerCase() || null,
      flags: userCheck.flags ?? {},
      source: 'user',
      totalPostsScanned: userCheck.totalPostsScanned ?? null,
      scannedAt: userCheck.lastScannedAt ?? userCheck.scannedAt ?? null,
      // When the newest check settled — set on EVERY settle, success or failure. Unlike `scannedAt`
      // (null unless a check produced a report), this advances on a failed run too, so it's the one
      // field that distinguishes one failed run from the next. Folded into the result signature so a
      // re-run — including a failed→failed re-run — reads as a genuinely new result. See
      // `brandSafetyResultSignature`.
      settledAt: userCheck.settledAt ?? userCheck.completedAt ?? null,
      checkType: userCheck.checkType ?? null,
      // Why the check failed (AAPU-6694) — surfaced in the row tooltip + progress modal. Carried on the
      // merged check so it flows on BOTH the REST snapshot and the live `.checks.updated` path (both store
      // the raw userCheck/pipelineCheck and merge on read). Snake-case fallback in case the wire differs.
      failureReason: userCheck.failureReason ?? userCheck.failure_reason ?? null,
    };
  }

  if (pipelineCheck && typeof pipelineCheck === 'object') {
    return {
      // The backend can report the pipeline scan's own live status (queued/running/succeeded/…), same
      // as userCheck, so a still-running pipeline scan gets the same "Running Brand Safety" loader +
      // progress modal. Persona's synthesized pipelineCheck (`toBrandSafetyBadgeData`, built from the
      // legacy flat `brandSafety` flags) carries no `status` at all — a stored terminal snapshot — so
      // an absent status still defaults to succeeded.
      status: String(pipelineCheck.status ?? '').toLowerCase() || 'succeeded',
      flags: pipelineCheck.flags ?? {},
      source: 'pipeline',
      totalPostsScanned: pipelineCheck.totalPostsScanned ?? null,
      scannedAt: pipelineCheck.scannedAt ?? null,
      settledAt: pipelineCheck.settledAt ?? pipelineCheck.completedAt ?? null,
      checkType: pipelineCheck.checkType ?? null,
      failureReason: pipelineCheck.failureReason ?? pipelineCheck.failure_reason ?? null,
    };
  }

  return null;
}

/**
 * The brand-safety check to render for a fetchCreators row (AAPU-1984) → `{ status, flags, ... } | null`.
 * `row.brandSafetyCheck` (merged via `mergeBrandSafetyCheck`) is the primary source; the legacy
 * `brandSafetyStatus`/`brandSafety`/`brandSafetyCheckedAt` fields are the last resort so rows from
 * before this rolled out keep rendering. Only the merged branch carries the extra
 * `source`/`totalPostsScanned`/`scannedAt`/`checkType` fields the redesigned column needs — the legacy
 * branch keeps returning the original `{ status, flags }` shape unchanged.
 */
export const readBrandSafetyCheck = (row) => {
  const merged = mergeBrandSafetyCheck(row?.brandSafetyCheck ?? {});
  if (merged) return merged;

  const status = String(row?.brandSafetyStatus ?? '').toLowerCase() || null;
  const flags = row?.brandSafety ?? {};
  if (status) return { status, flags };
  if (row?.brandSafetyCheckedAt != null || brandSafetyFlagTotal(flags) > 0) {
    return { status: 'succeeded', flags };
  }
  return null;
};

/**
 * `userCheck.checkType` (`"user:content_count"`, `"user:time_range"`, `"user:full"`, …) → the
 * "(… check)" suffix in the Low Risk pill label. Anything other than a `full` scope reads as a
 * "custom check" (content-count and time-range are both configured/custom scopes); an absent
 * checkType (legacy rows) defaults to "custom check" too, matching today's behavior.
 */
export function brandSafetyCheckTypeLabel(checkType) {
  const type = String(checkType ?? '')
    .toLowerCase()
    .split(':')
    .pop();
  return type === 'full' ? 'full check' : 'custom check';
}

/** A check's `scannedAt`/`lastScannedAt` timestamp → the column's compact "Aug 24" caption. */
export function formatBrandSafetyScanDate(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D') : null;
}

/**
 * The Brand Safety column's state for a row: `none` (never run) · `checking` (in progress) · `safe`
 * (succeeded or partially succeeded, no flags) · `alerts` (succeeded or partially succeeded, has
 * flags) · `failed` (failed/cancelled → retry). A partially-succeeded check renders identically to a
 * succeeded one — its report is already viewable, so there is nothing left to distinguish in the UI.
 */
export const brandSafetyCheckState = (row) => {
  const check = readBrandSafetyCheck(row);
  if (!check || !check.status) return 'none';
  if (RUNNING_CHECK_STATES.includes(check.status)) return 'checking';
  if (FAILED_CHECK_STATES.includes(check.status)) return 'failed';
  if (check.status === 'succeeded' || check.status === PARTIAL_CHECK_STATE) {
    return brandSafetyFlagTotal(check.flags) > 0 ? 'alerts' : 'safe';
  }
  return 'none';
};

// Tri-state: null when the backend hasn't sent the field, false while checking / never-run, true once
// the check reached a terminal state (safe/alerts/failed — a partially-succeeded check reads as
// safe/alerts, see brandSafetyCheckState).
export const hasBeenChecked = (record) => {
  if (
    record?.brandSafetyCheck === undefined &&
    record?.brandSafetyCheckedAt === undefined &&
    record?.brandSafetyStatus === undefined
  ) {
    return null;
  }
  const state = brandSafetyCheckState(record);
  return ['safe', 'alerts', 'failed'].includes(state);
};

/** Whether a row has a viewable brand-safety REPORT. A failed check has none. */
export const hasBrandSafetyResult = (record) =>
  ['safe', 'alerts'].includes(brandSafetyCheckState(record));

export const isServerSideChecking = (record) => brandSafetyCheckState(record) === 'checking';

/** Single source of truth for "is this row mid-check?" — Redux in-flight map OR the row's live status. */
export const isRowChecking = (record, runsInFlight) =>
  Boolean(runsInFlight?.[readCreatorReportDetailsId(record)]) || isServerSideChecking(record);

/**
 * Whether the Reverb-down fallback poll should run: the socket is NOT delivering live updates
 * (`connectionStatus !== 'connected'`) AND there is a check to watch — an optimistic in-flight run OR a
 * row the backend still reports as running (covers a reload / server-initiated run). Returns false when
 * connected (live events handle it) or when nothing is running (so a disconnected-but-idle socket never
 * polls).
 */
export const shouldPollBrandSafetyFallback = (connectionStatus, runsInFlight, influencers) => {
  if (connectionStatus === 'connected') return false;
  const hasInFlight = Boolean(runsInFlight) && Object.keys(runsInFlight).length > 0;
  const hasServerSide = Array.isArray(influencers) && influencers.some(isServerSideChecking);
  return hasInFlight || hasServerSide;
};

/**
 * During a re-run, the prior result to KEEP SHOWING (so a Safe/Alerts row isn't blanked to "Running")
 * → `{ state: 'safe' | 'alerts', flags } | null`. Prefers the pre-run result captured on the in-flight
 * entry (`startBrandSafetyRuns` snapshots it), which also identifies a prior *Safe* — the backend keeps
 * `flags` from the last succeeded check but flips `status` to running, so a prior Safe is otherwise
 * indistinguishable from a first run. Falls back to the row's retained non-empty flags (a prior Alerts)
 * for reload / server-initiated runs. Returns `null` for a first run, a prior-failed row, or not running.
 *
 * `priorOverride` lets a page that can't rely on the in-flight snapshot supply its own pre-run check:
 * `startBrandSafetyRuns` snapshots `priorCheck` off the Outreach slice's rows, which are empty on the
 * audience page, so that page keeps its own per-crd snapshot (`bsPriorChecksRef`) and passes it here —
 * without it, a previously-Safe row would blank to "Running" on re-run instead of showing "updating".
 */
export const readBrandSafetyRerunResult = (record, runsInFlight, priorOverride) => {
  const prior = priorOverride ?? runsInFlight?.[readCreatorReportDetailsId(record)]?.priorCheck;
  if (prior && ['succeeded', PARTIAL_CHECK_STATE].includes(prior.status)) {
    return {
      state: brandSafetyFlagTotal(prior.flags) > 0 ? 'alerts' : 'safe',
      flags: prior.flags ?? {},
    };
  }
  const live = readBrandSafetyCheck(record);
  if (live && RUNNING_CHECK_STATES.includes(live.status) && brandSafetyFlagTotal(live.flags) > 0) {
    return { state: 'alerts', flags: live.flags };
  }
  return null;
};

/**
 * A stable signature of a check's observed RESULT. `status` alone can't tell a re-run's own NEW outcome
 * from the stale pre-run result still on the row — a re-run of a failed (or succeeded) creator keeps that
 * terminal status until the backend finishes — so it also folds in the scan time, posts-scanned and flag
 * total, all of which advance on a fresh result. `settledAt` is the load-bearing one for FAILURES: a
 * failed check carries no scan time / posts / flags, so without it a failed→failed re-run signs
 * identically and the reconcile can never see the new run finish. `null`/status-less → `''`.
 */
export const brandSafetyResultSignature = (check) =>
  !check || !check.status
    ? ''
    : [
        check.status,
        check.scannedAt ?? '',
        check.totalPostsScanned ?? '',
        brandSafetyFlagTotal(check.flags),
        check.settledAt ?? '',
      ].join('|');

/**
 * Whether a row's current check is a genuinely NEW result vs. the check captured when the run started
 * (`priorCheck`). The reconcile paths gate completion on this so a re-run on an already-terminal
 * (failed/cancelled/succeeded) row isn't instantly "completed" by its own stale pre-run status before the
 * backend has reported the new run — which would delete the optimistic entry and, with it, stop the poll.
 */
export const isFreshBrandSafetyResult = (currentCheck, priorCheck) =>
  brandSafetyResultSignature(currentCheck) !== brandSafetyResultSignature(priorCheck);

// ─── Progress modal (live 4-stage check) ──────────────────────────────────────
/**
 * The four backend check stages, in order, that the live progress modal maps its steps to. These are the
 * only stages the backend reports (`queued` → `downloading_posts` → `running` → `succeeded`).
 */
export const BS_PROGRESS_STEPS = [
  { status: 'queued', label: 'Queued' },
  { status: 'downloading_posts', label: 'Downloading Posts' },
  { status: 'running', label: 'Analyzing' },
  { status: 'partially_succeeded', label: 'Partially Completed' },
  { status: 'succeeded', label: 'Succeeded' },
];

/**
 * Normalize a raw status for the progress stepper: `null`/empty (the optimistic gap right after Run,
 * before the first `.checks.updated`) → `'queued'`; the legacy `'processing'` alias → `'running'`.
 */
export const normalizeProgressStatus = (status) => {
  const s = String(status ?? '').toLowerCase();
  if (!s) return 'queued';
  if (s === 'processing') return 'running';
  return s;
};

/**
 * The visual state of one stepper row given the current live status:
 * `'completed'` (green check) · `'active'` (in progress) · `'pending'` · `'failed'`.
 * `succeeded` ⇒ every step completed. `failed`/`cancelled` ⇒ steps before `running` completed, the
 * `running` step `failed`, the rest pending (the backend doesn't report which stage failed).
 */
export const brandSafetyStepState = (stepStatus, currentStatus) => {
  const order = BS_PROGRESS_STEPS.map((s) => s.status);
  const cur = normalizeProgressStatus(currentStatus);
  const stepIdx = order.indexOf(stepStatus);
  if (cur === 'succeeded') return 'completed';
  const failed = FAILED_CHECK_STATES.includes(cur);
  const curIdx = failed ? order.indexOf('running') : order.indexOf(cur);
  if (stepIdx < curIdx) return 'completed';
  if (stepIdx === curIdx) return failed ? 'failed' : 'active';
  return 'pending';
};

// ─── Realtime ────────────────────────────────────────────────────────────────
/**
 * Whether Reverb is configured (build-time constant). When false no live
 * subscription is mounted — the Echo hooks eagerly instantiate a Pusher connector.
 */
export const REVERB_ENABLED = Boolean(process.env.REACT_APP_REVERB_KEY);

/** Private, one per campaign — matches `App\Events\BrandSafety\BrandSafetyChecksUpdated`'s channel. */
export const brandSafetyChannelName = (campaignId) =>
  campaignId != null && campaignId !== '' ? `brand-safety.${campaignId}` : null;

/**
 * Leading-dot form bypasses Laravel's app namespace, matching the existing
 * `.discovery.progress` / `.generation.progress` conventions. TODO(BE) confirm PROGRESS/COMPLETED/FAILED
 * — only CHECKS_UPDATED (`App\Events\BrandSafety\BrandSafetyChecksUpdated`) is documented/wired up.
 */
export const BS_EVENTS = {
  PROGRESS: '.brand-safety.progress',
  COMPLETED: '.brand-safety.completed',
  FAILED: '.brand-safety.failed',
  // Campaign Brand Safety screen: fires on every check status transition (queued → running → terminal),
  // for both pipeline and user-triggered checks. Each event is a DELTA — only the creators that just
  // changed, keyed by `creatorReportDetailsId` — so callers MERGE it into their view (patch the matched
  // creators, leave the rest untouched) and NEVER replace their whole view from it. A companion push to
  // the REST read endpoints, not a replacement; delivery is not guaranteed (the backend dispatch swallows
  // failures), so a missed push is "stale until the next poll/mount fetch," never an error state.
  CHECKS_UPDATED: '.checks.updated',
};

/** How often to re-sync from REST while any check is in flight. */
export const BS_POLL_INTERVAL = 10000;

export const readBrandSafetyProgressEvent = (event) => ({
  crd: readCreatorReportDetailsId(event),
  percent: event?.percent ?? event?.progress ?? null,
  batchId: event?.batchId ?? event?.batch_id ?? null,
});

export const readBrandSafetyCompletedEvent = (event) => ({
  crd: readCreatorReportDetailsId(event),
  ok: event?.failed !== true && String(event?.status ?? '').toLowerCase() !== 'failed',
  flagCount: event?.flagCount ?? event?.flags ?? null,
  message: event?.message ?? event?.error ?? null,
});

/**
 * `.checks.updated` payload → `{ campaignId, creatorChecks: [{ crd, status, flags, pipelineCheck,
 * userCheck }] }`. The wire payload is a DELTA — only the creators whose check just transitioned, one
 * entry per `creatorReportDetailsId`, each carrying its `pipelineCheck`/`userCheck` pair (the same shape
 * as the `brandSafetyCheck` field on `campaign/fetchCreators`) — so callers MERGE it by
 * `creatorReportDetailsId` (patch the matched rows, leave every other row untouched) and NEVER replace
 * their whole view from it. `status`/`flags` are the ONE check to render, merged via
 * `mergeBrandSafetyCheck` (userCheck wins over pipelineCheck), kept for callers that only need the
 * overlay status; `pipelineCheck`/`userCheck` are passed through unmerged for callers that store the
 * full per-creator check state (e.g. patching a row's `brandSafetyCheck` in place).
 */
export const readChecksUpdatedEvent = (event) => ({
  campaignId: event?.campaignId ?? event?.campaign_id ?? null,
  creatorChecks: (Array.isArray(event?.creatorChecks ?? event?.creator_checks)
    ? event.creatorChecks ?? event.creator_checks
    : []
  ).map((check) => {
    const pipelineCheck = check?.pipelineCheck ?? check?.pipeline_check ?? null;
    const userCheck = check?.userCheck ?? check?.user_check ?? null;
    const merged = mergeBrandSafetyCheck({ pipelineCheck, userCheck }) ?? {
      status: null,
      flags: {},
    };
    return {
      crd: readCreatorReportDetailsId(check),
      status: merged.status,
      flags: merged.flags,
      pipelineCheck,
      userCheck,
    };
  }),
});

// ─── Targets ─────────────────────────────────────────────────────────────────
/**
 * Normalizes whatever the caller passed to `open()` — a single row or an array —
 * into the modal's target shape. Rows without a `creatorReportDetailsId` are
 * dropped, since every brand-safety endpoint keys off it; the caller is told how
 * many were dropped so it can warn instead of silently under-running.
 */
export function toBrandSafetyTargets(rows) {
  if (!rows) return { targets: [], dropped: 0 };

  const list = Array.isArray(rows) ? rows : [rows];

  const targets = list
    .filter((row) => row && row.creatorReportDetailsId != null && row.creatorReportDetailsId !== '')
    .map((row) => ({
      creatorReportDetailsId: row.creatorReportDetailsId,
      // Retained for reference; the availability and run bodies now key on username + socialNetwork +
      // creatorReportDetailsId, so creatorReportsId is no longer sent.
      creatorReportsId: row.creatorReportsId ?? row.creator_reports_id ?? null,
      personaBuilderRequestId: row.builderRequestId ?? row.personaBuilderRequestId ?? null,
      // The availability and run endpoints key on username + socialNetwork.
      username: row.username ?? null,
      socialNetwork: row.socialNetwork ?? null,
      imageURL: row.imageURL ?? null,
      name: row.name ?? row.username ?? 'this influencer',
      availableContentCount: row.availableContentCount ?? null,
      // Already has a brand-safety result → the bulk modal shows it disabled and leaves it out of the
      // availability/run payloads (re-running an already-checked creator would just burn credits).
      hasResult: hasBrandSafetyResult(row),
    }));

  return { targets, dropped: list.length - targets.length };
}

/**
 * The posts ceiling, when the row payload reports one. With mixed inventory across a
 * bulk selection we take the MAXIMUM, not the minimum: bounding everyone to the
 * smallest library would stop the user asking for 200 posts because one creator has
 * 12. Creators with fewer posts are scanned in full. `null` means unbounded.
 */
export function maxPostsFromTargets(targets = []) {
  const counts = targets
    .map((target) => target.availableContentCount)
    .filter((count) => count != null && !Number.isNaN(Number(count)))
    .map(Number);

  return counts.length > 0 ? Math.max(...counts) : null;
}

/** True only when every target explicitly reports zero scannable content. */
export function hasNoScannableContent(targets = []) {
  if (targets.length === 0) return false;
  return targets.every((target) => target.availableContentCount === 0);
}

// ─── Validation ──────────────────────────────────────────────────────────────
export const isPostsCountValid = (value, max) =>
  Number.isInteger(value) && value >= MIN_POSTS && (max == null || value <= max);

export const isDateRangeValid = (start, end) =>
  Boolean(start && end) && !end.isBefore(start, 'day');

/** Whether the scope selection is complete and in bounds. Gates the Run button. */
export function isScopeValid(values = {}, maxPosts = null) {
  const { checkType, scope, postsCount, startDate, endDate } = values;

  if (checkType === BS_CHECK_TYPE.FULL) return true;
  if (checkType !== BS_CHECK_TYPE.CUSTOM) return false;

  if (scope === BS_SCOPE.CONTENT_COUNT) return isPostsCountValid(postsCount, maxPosts);
  if (scope === BS_SCOPE.TIME_RANGE) return isDateRangeValid(startDate, endDate);

  return false;
}

// ─── Phase machine ───────────────────────────────────────────────────────────
export const BS_PHASE = {
  IDLE: 'idle',
  CHECKING: 'checking', // waiting on the availability endpoint
  RESOLVING: 'resolving', // availability responded, but some creators are still pending (within poll budget)
  AVAILABILITY_ERROR: 'availabilityError', // availability call failed (e.g. 500)
  INVALID: 'invalid',
  NO_CREDITS: 'noCredits',
  ZERO_CONTENT: 'zeroContent',
  READY: 'ready',
  SUBMITTING: 'submitting',
};

/** Availability request lifecycle for a valid scope. `idle` = not attempted (do not gate on it). */
export const BS_AVAILABILITY = { IDLE: 'idle', LOADING: 'loading', OK: 'ok', ERROR: 'error' };

/**
 * The single phase that drives the whole modal.
 *
 * `availabilityStatus` gates Run on the availability endpoint: while it is loading the button is
 * disabled and a spinner shows; if it errored (e.g. a 500) the button stays disabled and an error is
 * shown — a failed availability check must never leave Run enabled. `idle` means availability was not
 * attempted (no scope yet, or a target without a handle), so it does not gate.
 */
export function deriveModalPhase({
  submitting = false,
  checkType = null,
  scopeValid = false,
  noScannableContent = false,
  balance = { known: false, remaining: null, unlimited: false, blocked: false },
  availabilityStatus = BS_AVAILABILITY.IDLE,
  pending = false,
  pollExhausted = false,
}) {
  if (submitting) return BS_PHASE.SUBMITTING;
  if (!checkType) return BS_PHASE.IDLE;

  // A pending/failed availability check gates Run regardless of anything else below.
  if (availabilityStatus === BS_AVAILABILITY.LOADING) return BS_PHASE.CHECKING;
  if (availabilityStatus === BS_AVAILABILITY.ERROR) return BS_PHASE.AVAILABILITY_ERROR;

  // Got a response, but some creators haven't resolved yet and we're still inside the poll budget —
  // block Run without discarding the (undercounted) totals we already have. Once `pollExhausted` flips
  // (or the backend settles `pending: false` on its own), this falls through to the normal checks below
  // using whatever the last response reported — the "unblock Run" behavior needs no special-casing.
  if (availabilityStatus === BS_AVAILABILITY.OK && pending && !pollExhausted) {
    return BS_PHASE.RESOLVING;
  }

  if (balance.blocked) return BS_PHASE.NO_CREDITS;
  // `insufficient` = out of / not enough credits (availability path); `blocked` = plan-not-available.
  if (balance.insufficient) return BS_PHASE.NO_CREDITS;
  if (!balance.unlimited && balance.known && balance.remaining === 0) return BS_PHASE.NO_CREDITS;

  if (noScannableContent) return BS_PHASE.ZERO_CONTENT;
  if (!scopeValid) return BS_PHASE.INVALID;

  return BS_PHASE.READY;
}

/** The one thing that enables the primary button. */
export const canRun = (phase) => phase === BS_PHASE.READY;

// ─── Copy ────────────────────────────────────────────────────────────────────
/**
 * The design contains two misspellings — "comperhensive" and "runing" — corrected
 * here. The verbatim design strings are kept in the comments so the deviation is
 * traceable.
 */
export const BS_COPY = {
  title: 'Run Brand Safety Check',

  // Design: "Configure and run a comperhensive brand safety analysis for this influencer."
  subtitleSingle: 'Configure and run a comprehensive brand safety analysis for this influencer.',
  subtitleBulk: (count) =>
    `Configure and run a comprehensive brand safety analysis for ${count} influencers.`,
  targetCountLine: (count) =>
    count === 1 ? 'Running check for 1 influencer' : `Running check for ${count} influencers`,

  // Design: "We recommend runing the full brand safety check after a verbal agreement
  //          and before sending a written contract."
  recommendation:
    'We recommend running the full brand safety check after a verbal agreement and before sending a written contract.',

  checkTypeLabel: 'Check Type',
  fullCheck: 'Full Check - Scans all available content',
  customCheck: 'Custom Check - Choose scope',
  byContentCount: 'By content count',
  byTimeRange: 'By time range',
  numberOfPosts: 'Number of posts:',
  latest: 'Latest',
  random: 'Random',
  startDate: 'Start Date',
  endDate: 'End Date',

  creditsRemaining: (remaining) =>
    `${Number(remaining).toLocaleString()} Brand Safety Credits Remaining`,
  creditsUnlimited: 'Unlimited Brand Safety Credits',
  // Per-check estimate from the availability endpoint (credits.estimatedCredits).
  estimatedCost: (credits) => `This check will use ~${Number(credits).toLocaleString()} credits.`,
  buyMoreCredits: 'Buy More Credits',

  cancel: 'Cancel',
  run: 'Run Brand Safety',
  running: 'Running…',

  clampedToAvailable: (max) => `Adjusted to the ${max} available posts`,

  // Unrestricted workspaces: instead of clamping the input, tell the user how many posts will actually be
  // scanned (and, on a shortfall, that fewer are available than they requested — no fake expectations).
  postsToScan: (n) => `We'll scan ${Number(n).toLocaleString()} post${Number(n) === 1 ? '' : 's'}.`,
  postsShortfall: (requested, available) =>
    `Only ${Number(available).toLocaleString()} of the ${Number(
      requested
    ).toLocaleString()} posts you requested are available — we'll scan ${Number(
      available
    ).toLocaleString()}.`,

  postsRequired: 'Enter the number of posts.',
  postsWholeNumber: 'Enter a whole number.',
  postsMin: `Enter at least ${MIN_POSTS} post.`,
  postsMax: (max) => `Only ${max} posts are available to scan.`,
  startDateRequired: 'Select a start date.',
  endDateRequired: 'Select an end date.',
  endBeforeStart: 'End date cannot be before the start date.',

  alreadyChecked: 'Already checked',
  alreadyCheckedSkipped: (count) =>
    count === 1
      ? '1 influencer already has a report and was skipped.'
      : `${count} influencers already have a report and were skipped.`,

  checkingAvailability: 'Checking availability…',
  availabilityError:
    "We couldn't check this influencer's availability. Please try again in a moment.",

  // Availability responded, but some creators are still resolving (within the poll budget).
  resolvingAvailability: "Still checking this influencer's content…",
  resolvingAvailabilityBulk: (resolved, total) =>
    `Calculating final cost — ${resolved} of ${total} influencers checked so far…`,
  // Shown once the poll budget is exhausted with some creators still unresolved.
  provisionalEstimateNote: (uncheckedCount) =>
    uncheckedCount === 1
      ? "Estimate excludes 1 influencer we couldn't check in time."
      : `Estimate excludes ${uncheckedCount} influencers we couldn't check in time.`,

  // Per-row status (bulk modal influencer list).
  rowCalculating: 'Calculating…',
  rowCouldNotCheck: "Couldn't check",
  rowNoContent: 'No content found',

  zeroContentSingle: 'This influencer has no available content to scan.',
  zeroContentBulk: 'None of the selected influencers have content to scan.',

  planBlocked: 'Brand safety checks are not available on your current plan.',
  noCreditsLeft: 'You have no Brand Safety credits remaining.',

  startedSingle: (name) => `Brand safety check started for ${name}.`,
  startedBulk: (count) => `Brand safety check started for ${count} influencers.`,
  startedPartial: (accepted, total, rejected) =>
    `Started for ${accepted} of ${total} influencers; ${rejected} could not be started.`,
  startFailed: 'Could not start the brand safety check. Please try again.',
  // AAPU-6693: distinct copy for a rate-limited (429) submission, kept separate from the generic failure
  // so the user knows to slow down rather than assume something broke. (Final wording → Dusan.)
  startRateLimited: "You're starting checks too quickly. Please wait a moment and try again.",
  noEligibleTargets: "These influencers are missing report data and can't be checked.",
  someIneligible: (dropped) =>
    dropped === 1
      ? '1 influencer was skipped because it is missing report data.'
      : `${dropped} influencers were skipped because they are missing report data.`,
  completeSingle: (name) => `Brand safety check complete for ${name}.`,
  failedSingle: (name) => `Brand safety check failed for ${name}.`,
  completeBulk: (completed, failed) =>
    failed > 0
      ? `Brand safety checks complete — ${completed} finished, ${failed} failed.`
      : `Brand safety checks complete — ${completed} finished.`,

  // Brand Safety column: a failed check offers a retry instead of a verdict.
  failedRetry: 'Failed, run again',
  failedRetryTooltip: 'The last check failed. Run it again.',
  // Reason copy for a failed run (AAPU-6694), mapped from the backend `failureReason` by
  // `brandSafetyFailureReason` so internal/third-party names (e.g. the post-sync provider) never reach the
  // UI. Known reasons map to these; an unmapped reason falls back to the raw string. (Final wording → Dusan.)
  failedReasonTimeout: 'This check took too long to complete and timed out. Please run it again.',
  failedReasonNoPosts: "We couldn't retrieve this creator's posts to scan. Please run it again.",

  // Brand Safety column result pill (AAPU-1984) — one tooltip per verdict. `totalPostsScanned` is
  // omitted from the sentence when the caller has no real count to report (e.g. Persona's baseline
  // scan, which carries no per-check metadata) rather than showing a misleading "0 posts".
  lowRiskTooltip: (totalPostsScanned) =>
    totalPostsScanned
      ? `Full account history scanned (${Number(
          totalPostsScanned
        ).toLocaleString()} total posts) with no high-risk content detected. Review our Brand Safety report for more information.`
      : 'Full account history scanned with no high-risk content detected. Review our Brand Safety report for more information.',
  issuesFlaggedTooltip: (count) =>
    `Found ${count} potential brand risk${
      count === 1 ? '' : 's'
    } in recent posts. Click to review flagged content.`,
  lowRiskSampleTooltip: (totalPostsScanned, username) =>
    totalPostsScanned
      ? `Only ${totalPostsScanned} post${
          Number(totalPostsScanned) === 1 ? '' : 's'
        } of @${username}'s library has been checked. Run a full Brand Safety Check to scan all posts for risks.`
      : `Only a sample of @${username}'s library has been checked. Run a full Brand Safety Check to scan all posts for risks.`,

  // Progress modal (ADD-26): the running-state column pill + the live 4-stage progress modal.
  runningColumn: 'Running Brand Safety',
  progressTooltip: 'Click to check details',
  progressTitle: 'Brand Safety Check',
  progressSubtitle: 'Running background intelligence check and media scanning',
  progressInProgress: 'Brand Safety Check in Progress',
  progressComplete: 'Brand Safety Check Complete',
  progressFailed: 'Brand Safety Check Failed',
  progressPhases: 'Analysis Phases',
  progressClose: 'Close',
  progressBanner: (email) =>
    `This may take a few minutes. We'll send you an email at ${email} when the Brand Safety report is ready.`,
  progressBannerGeneric:
    "This may take a few minutes. We'll send you an email when the Brand Safety report is ready.",
};

/**
 * Maps a backend `failureReason` to user-facing copy (AAPU-6694). Known reasons — a post-sync timeout, or
 * the sync yielding no posts — map to plain, jargon-free sentences; any other non-empty reason falls back
 * to the raw backend string (trimmed); an absent/empty reason returns `null` so callers can fall back to
 * the generic "the last check failed" tooltip. Keeping internal/third-party service names out of the UI is
 * the point of the mapping — never surface the raw provider name directly.
 */
export const brandSafetyFailureReason = (raw) => {
  const reason = typeof raw === 'string' ? raw.trim() : '';
  if (!reason) return null;
  if (/did not finish within|timed?[ -]?out|timeout/i.test(reason))
    return BS_COPY.failedReasonTimeout;
  if (/did not yield posts|0 succeeded|post-sync failed|no posts/i.test(reason)) {
    return BS_COPY.failedReasonNoPosts;
  }
  return reason;
};
